import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { HttpHeaderBuilder } from '../builders/http-headers.builder';
import { HttpBaseUrlProvider } from '../providers';
import { ToastService } from '@symphony-solo/shared/toast/src/lib/services/toast.service';
import { AlertService } from '@symphony-solo/shared/alert/src';
import { Capacitor } from '@capacitor/core';

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private REQUEST_TIMEOUT_IN_MILLISECONDS = 30000;
  private SERVICE_UNAVALIABLE_STATUS = 503;
  private SERVICE_HTTP_UNKNOWN_STATUS = 0;
  private platform = Capacitor.getPlatform();

  public constructor(
    private http: HttpClient,
    private urlProvider: HttpBaseUrlProvider,
    private toasterService: ToastService,
    private alertService: AlertService
  ) {}

  public makeGet<T>(urlPath: string, params?: {}, responseType: any = 'json', extraHeaders?: { [name: string]: any }): Observable<T> {
    return this.urlProvider.getUrl().pipe(
      switchMap((url) => {
        return this.http
          .get<T>(url + urlPath, {
            headers: this.createHeaders(extraHeaders),
            params,
            withCredentials: false,
            responseType,
          })
          .pipe(
            timeout(this.REQUEST_TIMEOUT_IN_MILLISECONDS),
            catchError((error) => {
              if (error instanceof TimeoutError) {
                this.toasterService.showErrorToastOnTop('Oops something went wrong! Please try again!', false);
              } else if (error.status === this.SERVICE_UNAVALIABLE_STATUS || error.status === this.SERVICE_HTTP_UNKNOWN_STATUS) {
                this.showServiceUnavaliableErrorAlert();
              }
              return throwError(error);
            })
          );
      })
    );
  }

  public makePost<T, U>(
    urlPath: string,
    body: U,
    responseType: any = 'json',
    extraHeaders?: { [name: string]: any }
  ): Observable<T | never> {
    return this.urlProvider.getUrl().pipe(
      switchMap((url) => {
        return this.http
          .post<T>(url + urlPath, body, {
            headers: this.createHeaders(extraHeaders),
            withCredentials: false,
            responseType,
          })
          .pipe(
            timeout(this.REQUEST_TIMEOUT_IN_MILLISECONDS),
            catchError((error) => {
              if (error instanceof TimeoutError) {
                this.toasterService.showErrorToastOnTop('Oops something went wrong! Please try again!', false);
              } else if (error.status === this.SERVICE_UNAVALIABLE_STATUS || error.status === this.SERVICE_HTTP_UNKNOWN_STATUS) {
                this.showServiceUnavaliableErrorAlert();
              }

              return throwError(error);
            })
          );
      })
    );
  }

  public makePostWithResponseHeaders<T, U>(
    urlPath: string,
    body: U,
    responseType: any = 'json',
    extraHeaders?: { [name: string]: any }
  ): Observable<any> {
    return this.urlProvider.getUrl().pipe(
      switchMap((url) => {
        return this.http
          .post<T>(url + urlPath, body, {
            observe: 'response',
            headers: this.createHeaders(extraHeaders),
            withCredentials: false,
            responseType,
          })
          .pipe(
            timeout(this.REQUEST_TIMEOUT_IN_MILLISECONDS),
            catchError((error) => {
              if (error instanceof TimeoutError) {
                this.toasterService.showErrorToastOnTop('Oops something went wrong! Please try again!', false);
              } else if (error.status === this.SERVICE_UNAVALIABLE_STATUS || error.status === this.SERVICE_HTTP_UNKNOWN_STATUS) {
                this.showServiceUnavaliableErrorAlert();
              }
              return throwError(error);
            })
          );
      })
    );
  }

  public makeDelete<T>(urlPath: string, params?: {}, responseType: any = 'json', extraHeaders?: { [name: string]: any }): Observable<T> {
    return this.urlProvider.getUrl().pipe(
      switchMap((url) => {
        return this.http
          .delete<T>(url + urlPath, {
            headers: this.createHeaders(extraHeaders),
            params,
            withCredentials: false,
            responseType,
          })
          .pipe(
            timeout(this.REQUEST_TIMEOUT_IN_MILLISECONDS),
            catchError((error) => {
              if (error instanceof TimeoutError) {
                this.toasterService.showErrorToastOnTop('Oops something went wrong! Please try again!', false);
              } else if (error.status === this.SERVICE_UNAVALIABLE_STATUS || error.status === this.SERVICE_HTTP_UNKNOWN_STATUS) {
                this.showServiceUnavaliableErrorAlert();
              }

              return throwError(error);
            })
          );
      })
    );
  }

  protected createHeaders(headers?: { [name: string]: string }): HttpHeaders {
    const builder = new HttpHeaderBuilder().create().addHeader('Accept', 'application/json').addHeader('Content-Type', 'application/json');
    if (headers) {
      // tslint:disable-next-line: forin
      for (const key in headers) {
        builder.addHeader(key, headers[key]);
      }
    }
    return builder.build();
  }

  private showServiceUnavaliableErrorAlert(): void {
    const retryButton = {
      text: 'Retry',
      cssClass: 'okButton centerButton',
      handler: async () => {
        window.location.reload();
      },
    };
    const noButton = {
      text: 'Close',
      cssClass: 'cancelBtn centerButton',
      handler: async () => {
        if (Capacitor.isNative) {
          navigator['app'].exitApp();
        }
      },
    };

    const buttons = [retryButton];
    if (this.platform === 'android') {
      buttons.push(noButton);
    }

    this.alertService.presentAlert(
      'Alert!',
      'Unable to communicate with our servers, check your connection & try again.',
      buttons,
      false,
      '',
      false
    );
  }
}
